"use client";

import React, {useMemo} from "react";
import { Stack } from "@mui/material";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import { useForm } from "react-hook-form";
import DataLayer from "@/components/data-layer/data-layer";
import needMoreInfoDataLayerPushFactory from "@/factories/data-layer/need-more-info-data-layer-push-factory";
import NeedMoreInfoSubmissionMessage from "@/components/forms/need-more-info/need-more-info-submission-message";
import NeedMoreInfoForm from "@/components/forms/need-more-info/need-more-info-form";
import {useLocale} from "next-intl";
import {NeedMoreInfoFormData} from "@/types/leads/lead-forms";
import useLeadSubmit from "@/hooks/forms/on-submit/use-lead-submit";

interface NMISectionProps {
  tourId: string;
  tourTitle: string;
}

const NeedMoreInfoSection = ({
  tourId,
  tourTitle
}: NMISectionProps) => {
  const sourceUrl = window.location.href;
  const locale = useLocale();
  const {
    handleSubmit,
    formState: { isSubmitSuccessful, isSubmitted, isSubmitting, isValid },
    control,
    getValues
  } = useForm<NeedMoreInfoFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isTravelAgent: "",
      optInEmail: locale === "en-us",
      workingWithAgent: false,
      withMoreTravelers: false,
      recaptchaToken: undefined,
      tourId,
      tourTitle
    }
  });

  const { onSubmit, serverError } = useLeadSubmit<NeedMoreInfoFormData>({
    locale,
    sourceUrl,
    leadType: "RAC"
  });
  
  const dataLayerData = useMemo(() => 
    needMoreInfoDataLayerPushFactory(getValues(), sourceUrl),
    [getValues, sourceUrl, isSubmitSuccessful]); // needs to update when isSubmitted changes

  return (
    <Stack
      sx={{
        gap: "2.4rem",
        width: "100%"
      }}
    >
      {/* Form and submission response */}
      {isSubmitSuccessful && (
        <NeedMoreInfoSubmissionMessage error={serverError} />
      )}
      {!isSubmitSuccessful && (
        <NeedMoreInfoForm
          isInvalid={!isValid && isSubmitted}
          control={control}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
        />
      )}
      {/* Privacy Policy */}
      {!isSubmitSuccessful && <HubspotFormDisclaimer testId="needMoreInfo-disclaimer" />}
      {isSubmitSuccessful && <DataLayer data={dataLayerData} />}
    </Stack>
  );
};

export default NeedMoreInfoSection;
